"use client";

import { useMemo } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import imgUnionBg from "@/assets/images/livescore/Union-bg.svg";
import imgCloseOutline from "@/assets/images/livescore/close-outline.svg";
import { buildOddsTripleBlock } from "./oddsPopupHelpers";
import { OddsDetailTable } from "./OddsDetailTable";
import { popupPanelClass, popupScrollClass, headTitleClass, closeBtnClass } from "./oddsPopupStyles";

export default function OddsChangePopup({
  match,
  visible,
  position,
  onClose,
  portal = false,
  gridLines = false,
  theme,
  themeStyle,
  onPanelEnter,
  onPanelLeave,
}) {
  const matchId = match?.id ?? "demo";
  const { getOdds } = useLiveDataForMatch(matchId);
  const liveOdds = getOdds();
  const companyIdOdds = useSelector((state) => state.bookmakerReducer);
  const bookmarkerExtend = useSelector((state) => state.bookmarkerExtendReducer) || "hongkong";

  const filterOdds = useFilterOdds(match ?? {}, liveOdds, null, companyIdOdds);
  const {
    filterHandicapOdds,
    filterOverUnderOdds,
    filterEuropeOdds,
    filterHandicapHalfOdds,
    filterOverUnderHalfOdds,
    filterEuropeHalfOdds,
  } = filterOdds;

  const odds = useMatchOdds(match ?? {}, filterOdds, companyIdOdds);

  const isAll = Number(companyIdOdds) === -1;

  const league = match?.league?.name || match?.leagueName || "—";
  const home = match?.homeName || "—";
  const away = match?.awayName || "—";

  const tripleBlock = useMemo(
    () =>
      buildOddsTripleBlock({
        match,
        isAll,
        filterHandicapOdds,
        filterOverUnderOdds,
        filterEuropeOdds,
        filterHandicapHalfOdds,
        filterOverUnderHalfOdds,
        filterEuropeHalfOdds,
        bookmarkerExtend,
        odds,
      }),
    [
      isAll,
      match,
      filterHandicapOdds,
      filterOverUnderOdds,
      filterEuropeOdds,
      filterHandicapHalfOdds,
      filterOverUnderHalfOdds,
      filterEuropeHalfOdds,
      bookmarkerExtend,
      odds,
    ],
  );

  if (!visible || !match) return null;

  const { hdp, ou, x12, hdpHt, ouHt, x12Ht } = tripleBlock;

  const node = (
    <div
      className={`pointer-events-none z-[99999] ${portal ? "fixed" : "absolute"}`}
      style={{
        top: `${position?.top ?? 0}px`,
        left: `${position?.left ?? 0}px`,
      }}
      onMouseEnter={onPanelEnter}
      onMouseLeave={onPanelLeave}
    >
      <div className={`pointer-events-auto ${popupPanelClass}`}>
        <div>
          <div className={headTitleClass} style={{ backgroundImage: `url(${imgUnionBg})` }}>
            <span>{league}</span>
          </div>
        </div>
        <button type="button" className={closeBtnClass} aria-label="Đóng" onClick={onClose}>
          <img src={imgCloseOutline} alt="" className="h-5 w-5" />
        </button>

        <table className="mt-[10px] mb-2 w-full border-0 border-collapse" cellPadding="0" cellSpacing="0" border="0">
          <tbody>
            <tr>
              <td className="kbv-cs-bg-header-bar px-2 py-2.5 text-center text-[13px] font-semibold text-[color:var(--theme-text)]">
                <span>{home}</span>
                <span className="mx-2.5">-</span>
                <span>{away}</span>
              </td>
            </tr>
          </tbody>
        </table>

        <div className={popupScrollClass}>
          <OddsDetailTable periodLabel="FT" hdp={hdp} ou={ou} x12={x12} gridLines={gridLines} />
          <OddsDetailTable periodLabel="HT" hdp={hdpHt} ou={ouHt} x12={x12Ht} gridLines={gridLines} />
        </div>
      </div>
    </div>
  );

  if (!portal || typeof document === "undefined") return node;

  return createPortal(
    <div
      className="keobongvip-root"
      data-theme={theme}
      style={{ background: "transparent", ...themeStyle }}
    >
      {node}
    </div>,
    document.body,
  );
}
